import { Info } from "lucide-react";

import { DeltaCell } from "@/components/cinch/DeltaCell";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import {
  useReplacementLineItems,
  type ReplacementLine,
  type ReplacementRow,
} from "../api";
import { fmtCur2 } from "../../renewals/format";

/** Item by item: what the replaced contract charged, what the account was
 *  actually billed just before signing, and what the replacement charges.
 *
 *  This exists so the headline net stops being a number you take on faith. On
 *  Moxie you can read straight down the column that the $9,540/mo is platform
 *  fees $7,750 → $17,100 plus reviews $2,030 → $3,220, less the text and landing
 *  page packages that went away.
 *
 *  Two things the columns have to be honest about:
 *
 *  - **Subscription and usage are subtotalled apart.** Only subscription is in
 *    the baseline the net is measured against; usage is shown at its rolling
 *    average for scale. Summing them together made the table foot to $11,208
 *    against a $10,780 headline.
 *  - **`base_fee` is not a line item** but bills as one, and is often the biggest
 *    single charge. It's folded onto the platform-fee row, and its moving
 *    between columns is usually most of the repricing. */
export function ReplacementLineTable({
  period,
  row,
}: {
  period: string;
  row: ReplacementRow;
}) {
  const q = useReplacementLineItems(period, row.contract_id);

  if (q.isLoading) {
    return <div className="h-48 animate-pulse rounded-md border bg-muted/30" />;
  }
  if (q.error) {
    return (
      <p className="rounded-md border border-dashed px-3 py-4 text-xs text-muted-foreground">
        Couldn't load the line-item comparison: {(q.error as Error).message}
      </p>
    );
  }
  const d = q.data;
  if (!d || d.lines.length === 0) {
    return (
      <p className="rounded-md border border-dashed px-3 py-4 text-xs text-muted-foreground">
        No billable items to compare — this account has no billing history and the
        contracts carry no line items.
      </p>
    );
  }

  // These should agree by construction. If they ever don't, say so rather than
  // showing a table that quietly disagrees with the number above it.
  const tiesBaseline =
    d.headline_baseline == null ||
    Math.abs(d.baseline_total - d.headline_baseline) < 1;
  const tiesChange =
    d.headline_net_change == null ||
    Math.abs(d.change_total - d.headline_net_change) < 1;

  return (
    <div className="space-y-2">
      {!d.has_contract_lines && (
        <p className="rounded-md border border-warning/40 bg-warning-light/40 px-3 py-2 text-xs text-warning">
          Cinch holds no line-item breakdown for these contracts, so the contract
          columns show totals only. The billed column is unaffected — it comes from
          the billing waterfall, not the contract.
        </p>
      )}

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Item</TableHead>
              <TableHead align="right">Replaced contract</TableHead>
              <TableHead align="right">
                Billed {d.prior_period ?? "prior period"}
              </TableHead>
              <TableHead align="right">Replacement</TableHead>
              <TableHead align="right">Change</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {d.lines
              .filter((l) => l.is_subscription)
              .map((l) => (
                <LineRow key={l.item_name} line={l} />
              ))}

            <TableRow className="border-t-2 bg-muted/40">
              <TableCell className="text-xs font-semibold">
                Subscription subtotal
              </TableCell>
              <TableCell numeric className="text-xs font-semibold">
                {d.replaced_total == null ? "—" : fmtCur2(d.replaced_total)}
              </TableCell>
              <TableCell numeric className="text-xs font-semibold">
                {fmtCur2(d.baseline_total)}
              </TableCell>
              <TableCell numeric className="text-xs font-semibold">
                {fmtCur2(d.replacement_total)}
              </TableCell>
              <TableCell numeric className="text-xs font-semibold">
                <DeltaCell value={d.change_total} threshold={0.01} />
              </TableCell>
            </TableRow>

            {d.lines.some((l) => !l.is_subscription) && (
              <>
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="bg-muted/20 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                  >
                    Usage · {d.usage_months}-month average, not part of the baseline
                  </TableCell>
                </TableRow>
                {d.lines
                  .filter((l) => !l.is_subscription)
                  .map((l) => (
                    <LineRow key={l.item_name} line={l} usage />
                  ))}
                <TableRow className="bg-muted/20">
                  <TableCell className="text-xs font-medium text-muted-foreground">
                    Expected usage
                  </TableCell>
                  <TableCell />
                  <TableCell numeric className="text-xs font-medium">
                    {fmtCur2(d.usage_total)}
                  </TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-muted-foreground">
        The billed column is what the account was actually invoiced in{" "}
        {d.prior_period ?? "the prior period"} — the baseline the net is measured
        against. Usage rows carry their {d.usage_months}-month average instead,
        since the single month before signing is as likely to be $0 as
        representative, and they sit outside the baseline.
      </p>

      {(!tiesBaseline || !tiesChange) && (
        <p className="flex items-start gap-1.5 rounded-md border border-danger/40 bg-danger-light/40 px-3 py-2 text-xs text-danger">
          <Info className="mt-px h-3.5 w-3.5 flex-shrink-0" />
          <span>
            This table doesn't reconcile to the figures above it
            {!tiesBaseline &&
              ` — baseline ${fmtCur2(d.baseline_total)} vs ${fmtCur2(d.headline_baseline)}`}
            {!tiesChange &&
              ` — change ${fmtCur2(d.change_total)} vs ${fmtCur2(d.headline_net_change)}`}
            . Treat the headline as authoritative and flag this row.
          </span>
        </p>
      )}
    </div>
  );
}

function LineRow({
  line,
  usage = false,
}: {
  line: ReplacementLine;
  usage?: boolean;
}) {
  return (
    <TableRow className={cn(line.is_remainder && "text-muted-foreground")}>
      <TableCell className="text-xs">
        <span className="block">{line.item_name}</span>
        {line.is_base_fee && (
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            contract base fee
          </span>
        )}
        {line.is_remainder && (
          <span className="text-[10px] text-warning">
            no line items in Cinch for this portion
          </span>
        )}
      </TableCell>
      <TableCell numeric className="text-xs">
        {line.replaced_mrr == null ? "—" : fmtCur2(line.replaced_mrr)}
      </TableCell>
      <TableCell numeric className="text-xs">
        {line.baseline_mrr == null ? (
          "—"
        ) : usage ? (
          <span title={`Billed ${fmtCur2(line.billed_prior)} that month`}>
            {fmtCur2(line.baseline_mrr)}
          </span>
        ) : (
          fmtCur2(line.baseline_mrr)
        )}
      </TableCell>
      <TableCell numeric className="text-xs">
        {line.replacement_mrr == null ? "—" : fmtCur2(line.replacement_mrr)}
      </TableCell>
      <TableCell numeric className="text-xs">
        {usage ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <DeltaCell value={line.change_mrr} threshold={0.01} />
        )}
      </TableCell>
    </TableRow>
  );
}
